/**
 * Provenance normalizado para el módulo de exportación.
 *
 * El modelo Task de la app usa cadenas legibles (es-ES) en su campo `source`,
 * con la convención "(Modificada)" tras una edición manual. Para los exporters
 * necesitamos un identificador estable, machine-readable e independiente del
 * idioma.
 *
 * Esta utilidad expone:
 *   - el enum normalizado (ExportProvenance)
 *   - normalizeProvenance(source): traduce la cadena original al enum
 *   - provenanceLabel(p): etiqueta humana (es-ES) usada en CSV/XLSX
 */

export enum ExportProvenance {
  AI = 'AI',
  AI_MODIFIED = 'AI_MODIFIED',
  HISTORIC = 'HISTORIC',
  HISTORIC_MODIFIED = 'HISTORIC_MODIFIED',
  MANUAL = 'MANUAL',
  MANUAL_MODIFIED = 'MANUAL_MODIFIED',
  UNKNOWN = 'UNKNOWN'
}

/**
 * Traduce el valor literal de `Task.source` al enum normalizado.
 * Tolerante a acentos, capitalización y al sufijo "(Modificada)" presente o no.
 */
export function normalizeProvenance(source: string | undefined | null): ExportProvenance {
  const raw = (source || '').trim().toLowerCase();
  if (!raw) return ExportProvenance.UNKNOWN;

  const isModified = /modificada/.test(raw);
  // Quitamos diacríticos para que "Histórica" y "Historica" colapsen al mismo prefijo.
  const noAccents = raw.normalize('NFD').replace(/[\u0300-\u036f]/g, '');

  if (noAccents.startsWith('ia')) {
    return isModified ? ExportProvenance.AI_MODIFIED : ExportProvenance.AI;
  }
  if (noAccents.startsWith('historica')) {
    return isModified ? ExportProvenance.HISTORIC_MODIFIED : ExportProvenance.HISTORIC;
  }
  if (noAccents.startsWith('manual')) {
    return isModified ? ExportProvenance.MANUAL_MODIFIED : ExportProvenance.MANUAL;
  }
  return ExportProvenance.UNKNOWN;
}

/** Etiqueta humana (es-ES) para formatos legibles (CSV/XLSX). */
export function provenanceLabel(p: ExportProvenance): string {
  switch (p) {
    case ExportProvenance.AI: return 'IA';
    case ExportProvenance.AI_MODIFIED: return 'IA (Modificada)';
    case ExportProvenance.HISTORIC: return 'Histórica';
    case ExportProvenance.HISTORIC_MODIFIED: return 'Histórica (Modificada)';
    case ExportProvenance.MANUAL: return 'Manual';
    case ExportProvenance.MANUAL_MODIFIED: return 'Manual (Modificada)';
    default: return 'Desconocido';
  }
}
